export default function getScrews({group, path, circle, screwTypeOptionId, rectWidth, rectHeight, productIsLamp = false, customInteraxis = 0}) {
  // Configuration for multi-column screw layouts
  const MULTI_COLUMN_CONFIGS = [
    { width: 117, height: 117, horizontalSpacing: 23, verticalSpacing: 41.5, columnOffsets: [-1, 1] },
    { width: 162, height: 117, horizontalSpacing: 46, verticalSpacing: 41.5, columnOffsets: [-1, 0, 1] },
    { width: 208, height: 117, horizontalSpacing: 46, verticalSpacing: 41.5, columnOffsets: [-1.5, -0.5, 0.5, 1.5] },
    { width: 254, height: 117, horizontalSpacing: 43, verticalSpacing: 41.5, columnOffsets: [-2, -1, 0, 1, 2] }
  ];

  var axis = rectWidth >= rectHeight ? 'X' : 'Y';
  if (productIsLamp && !(rectWidth === 82 && rectHeight === 82)) axis = 'Y';

  var screwSpacing;
  if ((rectWidth >= rectHeight ? rectWidth : rectHeight) === 82) screwSpacing = 60;
  if ((rectWidth >= rectHeight ? rectWidth : rectHeight) === 91) screwSpacing = 66;
  if ((rectWidth >= rectHeight ? rectWidth : rectHeight) === 117) screwSpacing = 83;
  if ((rectWidth >= rectHeight ? rectWidth : rectHeight) === 133) screwSpacing = 107;
  if ((rectWidth >= rectHeight ? rectWidth : rectHeight) === 144) screwSpacing = 117;
  if ((rectWidth >= rectHeight ? rectWidth : rectHeight) === 175) screwSpacing = 150;
  if ((rectWidth >= rectHeight ? rectWidth : rectHeight) === 207) screwSpacing = 170;

  if (customInteraxis !== 0) screwSpacing = customInteraxis;

  var screwBasedOnType = {5: 'torxScrew', 6: 'slotHeadScrew'};
  var screwRadius = 3.3;

  // Helper function to generate screw positions for multi-column layouts
  function generateMultiColumnScrewPositions(rectWidth, rectHeight, horizontalSpacing, verticalSpacing, columnOffsets, screwRadius) {
    var positions = {};
    var letters = 'abcdefghij'.split('');
    var index = 0;

    columnOffsets.forEach(function(offset) {
      var xOffset = offset * horizontalSpacing;
      // Top screw
      positions[letters[index++]] = {
        x: (rectWidth / 2) + xOffset - screwRadius,
        y: (rectHeight / 2) - verticalSpacing - screwRadius
      };
      // Bottom screw
      positions[letters[index++]] = {
        x: (rectWidth / 2) + xOffset - screwRadius,
        y: (rectHeight / 2) + verticalSpacing - screwRadius
      };
    });

    return positions;
  }

  var screwPositions;

  // Check if this plate size uses multi-column layout
  var config = MULTI_COLUMN_CONFIGS.find(function(c) {
    return c.width === rectWidth && c.height === rectHeight;
  });

  if (config) {
    screwPositions = generateMultiColumnScrewPositions(
      rectWidth,
      rectHeight,
      config.horizontalSpacing,
      config.verticalSpacing,
      config.columnOffsets,
      screwRadius
    );
  }
  else {
    // Standard 2-screw layout
    screwPositions = {
      a: {
        x: (rectWidth / 2) - (axis === 'X' ? (screwSpacing / 2) : 0) - screwRadius,
        y: (rectHeight / 2) - (axis === 'Y' ? (screwSpacing / 2) : 0) - screwRadius
      },
      b: {
        x: (rectWidth / 2) + (axis === 'X' ? (screwSpacing / 2) : 0) - screwRadius,
        y: (rectHeight / 2) + (axis === 'Y' ? (screwSpacing / 2) : 0) - screwRadius
      }
    };
  }

  function calcStarPoints(centerX, centerY, innerCirclePoints, innerRadius, outerRadius) {
    var angle = (Math.PI / innerCirclePoints);
    var angleOffsetToCenterStar = 0;

    var totalPoints = innerCirclePoints * 2; // 10 in a 5-points star

    var points = [];
    for (let i = 0; i < totalPoints; i++) {
      var isEvenIndex = i % 2 == 0;
      var r = isEvenIndex ? outerRadius : innerRadius;
      var currX = centerX + Math.cos(i * angle + angleOffsetToCenterStar ) * r;
      var currY = centerY + Math.sin(i * angle + angleOffsetToCenterStar) * r;

      if (i === 0) {
        points.push({command: 'M', x: currX, y: currY});
      }
      else {
        points.push({command: 'L', x: currX, y: currY});
      }
    }

    var firstPoint = _.first(points);
    points.push({command: 'L', x: firstPoint.x, y: firstPoint.y});

    return points;
  }

  function calculateLineXYOnCircle({cx, cy, angle, radius}) {
    angle = angle * (Math.PI / 180);
    return {
      x: cx + (radius * Math.cos(angle)),
      y: cy + (radius * Math.sin(angle))
    };
  }

  var screws = {
    torxScrew: ({x, y}) => group({width: screwRadius * 2, height: screwRadius * 2, x, y}, ([
      circle({cx: screwRadius, cy: screwRadius, r: screwRadius, stroke: 'black', strokeWidth: 1, fill: 'transparent'}),
      path({d: calcStarPoints(screwRadius, screwRadius, 6, 1, 1.7), strokeWidth: 1, stroke: 'black', fill: 'transparent'})
    ])),

    slotHeadScrew: ({x, y}) => {
      var firstPoint = calculateLineXYOnCircle({cx: screwRadius, cy: screwRadius, angle: 278, radius: screwRadius});
      var secondPoint = calculateLineXYOnCircle({cx: screwRadius, cy: screwRadius, angle: 82, radius: screwRadius});
      var thirdPoint = calculateLineXYOnCircle({cx: screwRadius, cy: screwRadius, angle: 264, radius: screwRadius});
      var fourthPoint = calculateLineXYOnCircle({cx: screwRadius, cy: screwRadius, angle: 96, radius: screwRadius});

      return group({width: 8, height: 8, x, y}, ([
        circle({cx: screwRadius, cy: screwRadius, r: screwRadius, stroke: 'black', strokeWidth: 1, fill: 'transparent'}),
        path({d: [
          {command: 'M', x: firstPoint.x, y: firstPoint.y},
          {command: 'L', x: secondPoint.x, y: secondPoint.y},
        ], strokeWidth: 1, stroke: 'black', fill: 'transparent'}),
        path({d: [
          {command: 'M', x: thirdPoint.x, y: thirdPoint.y},
          {command: 'L', x: fourthPoint.x, y: fourthPoint.y},
        ], strokeWidth: 1, stroke: 'black', fill: 'transparent'})
      ]));
    }
  };

  var returnObject;

  // Check if we should skip screws entirely
  if (screwTypeOptionId === 103 || screwTypeOptionId === 0) {
    returnObject = [];
  }
  else {
    // Generate screw elements from positions using map
    var screwElements = Object.keys(screwPositions).map(function(key) {
      return screws[screwBasedOnType[screwTypeOptionId]](screwPositions[key]);
    });

    returnObject = group({x: 0, y: 0}, screwElements);
  }

  return returnObject;
}